import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";

// Fallback to current origin if env is missing (helps on localhost)
const REDIRECT_URL =
  process.env.REACT_APP_REDIRECT_URL || window.location.origin;

const Login = () => {
  const [email, setEmail] = useState("");
  const [activeTab, setActiveTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [showEmailRecovery, setShowEmailRecovery] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const checkProfile = async (session) => {
      const { data: profile } = await supabase
        .from("creator_profiles")
        .select("id")
        .eq("user_id", session.user.id)
        .single();
      if (profile) navigate("/dashboard");
      else navigate("/profile");
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) checkProfile(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) checkProfile(session);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleMagicLink = async () => {
    if (!email) {
      alert("Please enter your email.");
      return;
    }
  
    setLoading(true);
    setSuccessMessage("");
  
    try {
      // 1️⃣ Ask backend (service role) if this email already has a user
      const { data: exists, error: rpcError } = await supabase.rpc(
        "check_user_exists",
        {
          email_input: email, // 🔹 must match the function argument name
        }
      );
  
      if (rpcError) {
        console.error("check_user_exists RPC error:", rpcError);
        // You can decide to block here; for now we allow signup but show a generic message if needed
        // setLoading(false);
        // alert("Unable to verify your email right now. Please try again.");
        // return;
      }
  
      if (exists === true) {
        // 2️⃣ Existing user → do NOT send signup link
        setLoading(false);
        alert("An account with this email already exists. Please log in instead.");
        setActiveTab("login");
        return;
      }
  
      // 3️⃣ New user → safe to send magic sign-up link
      const { error: signupError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          emailRedirectTo: `${REDIRECT_URL}/set-password`,
        },
      });
  
      setLoading(false);
  
      if (signupError) {
        console.error("Magic link signup error:", signupError);
        alert(
          signupError.message ||
            "Unable to send magic link. Please try again in a moment."
        );
      } else {
        setSuccessMessage(
          "Check your email for the magic sign-up link. If it’s not in your inbox, check your Spam or Promotions folder."
        );
      }
    } catch (err) {
      console.error("Unexpected error in handleMagicLink:", err);
      setLoading(false);
      alert(
        "Something went wrong while sending the sign-up link. Please try again."
      );
    }
  };
  
  

  const handleGoogleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        // Comes back to /login so Login's useEffect can route to /dashboard or /profile
        redirectTo: `${REDIRECT_URL}/login`,
      },
    });
    if (error) alert(error.message);
  };

  const handleForgotPassword = async () => {
    if (!email) return alert("Please enter your email.");
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${REDIRECT_URL}/set-password`,
    });
    if (error) alert(error.message);
    else {
      setSuccessMessage("Password reset email sent successfully.");
      setShowEmailRecovery(false);
      setEmail("");
    }
  };

  const isSignup = activeTab === "signup";

  return (
    <Layout>
      <div className="min-h-screen w-screen overflow-x-hidden bg-gradient-to-br from-[#020617] via-[#050816] to-black font-inter text-slate-100 flex flex-col">
        {/* Top mini-nav */}
        <header className="w-full max-w-5xl mx-auto flex items-center justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <img
              src="/Dreamframelogo.png"
              alt="Dreamframe Studio Logo"
              className="h-8 w-8 rounded-md shadow-[0_0_16px_rgba(124,58,237,0.6)] object-cover"
            />
            <span className="text-sm font-semibold tracking-wide text-cyan-200">
              Dreamframe Studio
            </span>
          </div>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="rounded-2xl border border-slate-500/70 bg-slate-900/80 px-3 py-1.5 text-xs font-medium text-slate-100 hover:bg-slate-800 hover:border-cyan-300/70"
          >
            ← Back to main site
          </button>
        </header>

        {/* Centered auth card */}
        <div className="flex-1 w-full flex justify-center items-center pt-10 pb-24 md:pt-6 md:pb-24 box-border px-4">
          <div className="w-full max-w-md rounded-3xl border border-white/10 bg-slate-900/90 px-6 py-7 shadow-[0_18px_45px_rgba(0,0,0,0.85)] backdrop-blur">
            {/* Logo */}
            <div className="flex justify-center mb-4">
              <img
                src="/Dreamframelogo.png"
                alt="Dreamframe Studio Logo"
                className="h-16 w-16 rounded-xl object-cover shadow-[0_0_30px_rgba(129,140,248,0.7)]"
              />
            </div>

            {/* Tabs */}
            <div className="mb-5 inline-flex w-full rounded-full bg-slate-900/80 p-1 text-xs">
              <button
                type="button"
                onClick={() => {
                  setActiveTab("login");
                  setSuccessMessage("");
                  setShowEmailRecovery(false);
                }}
                className={`flex-1 rounded-full px-3 py-2 font-semibold transition ${
                  activeTab === "login"
                    ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-[0_0_18px_rgba(56,189,248,0.8)]"
                    : "text-slate-300 hover:text-slate-100"
                }`}
              >
                Login
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveTab("signup");
                  setSuccessMessage("");
                  setShowEmailRecovery(false);
                }}
                className={`flex-1 rounded-full px-3 py-2 font-semibold transition ${
                  activeTab === "signup"
                    ? "bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white shadow-[0_0_18px_rgba(236,72,153,0.8)]"
                    : "text-slate-300 hover:text-slate-100"
                }`}
              >
                Sign Up
              </button>
            </div>

            {/* Heading */}
            <h2 className="text-center text-lg font-semibold text-sky-200 mb-2">
              {isSignup
                ? "🪄 Sign up to Dreamframe Studio"
                : "🔐 Log in to Dreamframe Studio"}
            </h2>
            <p className="text-center text-[11px] text-slate-400 mb-4">
              One account for everything: post projects, join collaborations,
              and unlock AI-powered production workflows.
            </p>

            {/* Email + Magic Link / Password Reset */}
            {(isSignup || showEmailRecovery) && (
              <div className="space-y-3 mb-4">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-slate-200">
                    Email address
                  </label>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
                  />
                </div>
                <button
                  type="button"
                  onClick={isSignup ? handleMagicLink : handleForgotPassword}
                  disabled={loading || !email}
                  className="w-full rounded-2xl bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-pink-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-fuchsia-500/40 hover:brightness-110 disabled:opacity-60"
                >
                  {loading
                    ? "Sending..."
                    : isSignup
                    ? "Send Magic Sign-Up Link"
                    : "Send Password Reset Link"}
                </button>
              </div>
            )}

            {/* Success message */}
            {successMessage && (
              <p className="mb-3 text-[12px] text-emerald-300">
                {successMessage}
              </p>
            )}

            {/* Divider */}
            <div className="my-3 flex items-center gap-2 text-[11px] text-slate-400">
              <span className="h-px flex-1 bg-slate-700/70" />
              <span>or</span>
              <span className="h-px flex-1 bg-slate-700/70" />
            </div>

            {/* Google Login */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="mb-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-600 bg-slate-950/80 px-4 py-2.5 text-sm font-semibold text-slate-100 shadow-sm hover:bg-slate-900"
            >
              <img
                src="https://fonts.gstatic.com/s/i/productlogos/googleg/v6/24px.svg"
                alt="Google logo"
                className="h-4 w-4"
              />
              <span>Continue with Google</span>
            </button>

            {/* Email + Password login option (for existing users) */}
            {activeTab === "login" && !showEmailRecovery && (
              <>
                <p className="mt-2 text-[12px] text-slate-300">
                  Already have a password?
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/email-login")}
                  className="mt-1 w-full rounded-2xl border border-slate-600 bg-slate-900 px-4 py-2.5 text-sm font-semibold text-slate-100 hover:bg-slate-800"
                >
                  Login with Email + Password
                </button>
              </>
            )}

            {/* Forgot / Recovery toggle */}
            {activeTab === "login" && (
              <div className="mt-4 text-center text-[12px]">
                {!showEmailRecovery ? (
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailRecovery(true);
                      setSuccessMessage("");
                    }}
                    className="text-cyan-300 underline underline-offset-4 hover:text-cyan-200"
                  >
                    Forgot your password or user ID?
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailRecovery(false);
                      setSuccessMessage("");
                      setEmail("");
                    }}
                    className="text-fuchsia-300 underline underline-offset-4 hover:text-fuchsia-200"
                  >
                    🔁 Return to other login options
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Login;
